import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  interpolate,
  pickLang,
  translations,
} from "./translations";

const LanguageContext = createContext(null);

const readSavedLang = () => {
  try {
    const saved = localStorage.getItem("appLanguage");
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;
  } catch (_) {}
  return DEFAULT_LANGUAGE;
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readSavedLang);

  useEffect(() => {
    try {
      localStorage.setItem("appLanguage", lang);
    } catch (_) {}
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const entry = translations[key];
      if (!entry) return key;
      const str = entry[lang] ?? entry[DEFAULT_LANGUAGE] ?? "";
      return vars ? interpolate(str, vars) : str;
    },
    [lang]
  );

  const tField = useCallback((value) => pickLang(value, lang), [lang]);

  const value = useMemo(() => ({ lang, setLang, t, tField }), [lang, setLang, t, tField]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within LanguageProvider");
  }
  return ctx;
}
